/* eslint-disable camelcase */
/* eslint-disable react/prop-types */
import { useEffect, useMemo, useRef, useState } from 'react';
import styles from './styles';
import { Box, Drawer, Fab, FormControl, Grid, IconButton, InputLabel, MenuItem, Select, Typography } from '@mui/material';
import PropTypes from 'prop-types';
import { ArrowDropUp, CancelPresentationRounded, ChevronLeft, ChevronRight, Circle, Close, KeyboardArrowUp, LinearScaleOutlined, Square } from '@mui/icons-material';
import { HYDRAULICS_CHART_COLORS, STRIPPING_TRIP_TYPE_COLORS } from '../../../../shared/config/hydraulics_constants';
import { getOptimalInterval } from '../../../../utils/getOptimalInterval';
import { CHART_MARKERTYPES, STRIPPING_RUN_MAP, STRIPPING_TYPE_PRIMARY, STRIPPING_TYPE_SECONDARY } from '../../../../utils/constants';
import { getMinMaxOfKey } from '../../../../utils/objectHelpers';
import CanvasJSReact from '../../../ui/canvasjs/canvasjs.react';
import ChartsAxisPopover from '../../../ui/ChartsAxisPopover';
import Datagrid from '../../../ui/Datagrid';
import { useSelector } from 'react-redux';
import { isMobile } from 'react-device-detect';
import { COLORS } from '../../../../shared/config/colors';

const CanvasJSChart = CanvasJSReact.CanvasJSChart;

const FeaturedWellStrippingChart = ({
  data,
  window,
  units,
}) => {
  const classes = styles();
  const [tableRow, setTableRows] = useState([]);
  const [showTable, setShowTable] = useState(true);
  const strippingResults = data.results;
  const strippingUnits = units.stripping;
  const windowUnits = units.windows;
  const [plotForPrimary, setPlotForPrimary] = useState(STRIPPING_TYPE_PRIMARY[0]?.label);
  const [plotForSecondary, setPlotForSecondary] = useState('Hole Depth');
  const [chartData, setChartData] = useState([]);
  const containerRef = useRef(null);
  const scrollPositionRef = useRef(0);
  const [tableColumn, setTableColumn] = useState([]);
  const [minMax_X, setMinMax_X] = useState({});
  const [chartLegendData, setChartLegendData] = useState([]);
  const [hiddenChartData, setHiddenChartData] = useState([]);
  const [secondaryDepthField, setSecondaryDepthField] = useState(STRIPPING_TYPE_SECONDARY);
  const hydraulicsLanding = useSelector(state => state.hydraulicsLanding);
  const [minMax_Y, setMinMax_Y] = useState({});
  const [defaultXAxis, setDefaultXAxis] = useState({});
  const [defaultYAxis, setDefaultYAxis] = useState({});
  const [chartXAxis, setChartXAxis] = useState({
    min: '',
    max: ''
  });
  const [chartYAxis, setChartYAxis] = useState({
    min: '',
    max: ''
  });
  const [showMobileChartTable, setShowMobileChartTable] = useState(false);

  const handleScroll = () => {
    if (containerRef.current) {
      scrollPositionRef.current = containerRef.current.scrollTop;
    }
  };

  useEffect(() => {
    if (minMax_X) setDefaultXAxis(minMax_X);
    if (minMax_Y) setDefaultYAxis(minMax_Y);
  }, [minMax_X, minMax_Y]);

  const [chartConfig, setChartConfig] = useState([
    {
      name: 'PP',
      key: 'pp',
      color: HYDRAULICS_CHART_COLORS.PORE_PRESSURE,
      hide: false,
      tooltip: `MD: {y} ${windowUnits?.md?.label} <br/> PP: {x} ${windowUnits?.pp?.label}`
    },
    {
      name: 'FP',
      key: 'fp',
      color: HYDRAULICS_CHART_COLORS.FRACTURE_PRESSURE,
      hide: false,
      tooltip: `MD: {y} ${windowUnits?.md?.label} <br/> FP: {x} ${windowUnits?.fp?.label}`
    },
  ]);

  useEffect(() => {
    const shouldShow = primaryField.field === 'ecd_with_sbp' || primaryField.field === 'esd_with_sbp';
    setChartConfig(prevItems =>
      prevItems.map(item => ({
        ...item,
        hide: !shouldShow
      }))
    );
  }, [plotForPrimary]);

  const primaryField = STRIPPING_TYPE_PRIMARY.find(
    (type) => type.label === plotForPrimary
  );

  const secondaryField = secondaryDepthField.find(
    (type) => type.label === plotForSecondary
  );

  useEffect(() => {
    if (!strippingResults?.length) return;
    const holeDepthUnit = strippingUnits?.results?.depths_summary?.hole_depth;
    const secondaryFieldsMap = new Map();

    strippingResults.forEach((item) => {
      item.results.forEach((resultItem) => {
        // eslint-disable-next-line no-unused-vars
        const { casing_shoe, hole_depth, landing_point, ...restDepths } =
          resultItem.depths_summary || {};

        Object.entries(restDepths).forEach(([key]) => {
          const normalizedKey = key.toLowerCase();
          const label = key.replace(/_/g, ' ').toUpperCase();

          if (!secondaryFieldsMap.has(normalizedKey)) {
            secondaryFieldsMap.set(normalizedKey, { field: normalizedKey, label });
          }

          if (!strippingUnits?.results?.depths_summary?.[normalizedKey]) {
            strippingUnits.results.depths_summary[normalizedKey] = holeDepthUnit;
          }
        });
      });
    });

    const dynamicSecondaryFields = Array.from(secondaryFieldsMap.values());

    setSecondaryDepthField([
      ...STRIPPING_TYPE_SECONDARY,
      ...dynamicSecondaryFields,
    ]);
  }, [strippingResults, strippingUnits]);

  useEffect(() => {
    if (!hydraulicsLanding?.loading && !strippingResults?.length) return;
    const depth_summary_units = strippingUnits?.results?.depths_summary;

    const typeCountMap = {};
    const graphData = strippingResults?.map(({ results = [], inputs = {} }, index) => {
      const tripType = inputs?.trip_data?.type;
      if (!typeCountMap[tripType]) {
        typeCountMap[tripType] = 0;
      }
      const markerIndex = typeCountMap[tripType] % CHART_MARKERTYPES.length;
      const markerType = CHART_MARKERTYPES[markerIndex];

      typeCountMap[tripType] += 1;
      return {
        inputs,
        index,
        runNumber: index + 1,
        markerType,
        markerSize: 8,
        hide: false,
        color: STRIPPING_TRIP_TYPE_COLORS[tripType],
        type: 'line',
        toolTipContent: `Run-${index + 1}: ${inputs?.trip_data?.speed} ${strippingUnits?.inputs?.speed?.label} <br/> Bit Depth: {y} ${strippingUnits?.results?.depth_points?.md?.label} <br/> ${(primaryField?.name ?? primaryField?.label) + ' @ ' + (secondaryField?.label)}: {x} ${depth_summary_units?.[secondaryField?.field]?.[primaryField?.field]?.label} <br/>`,
        depth_points: results.map((item) => ({
          ...item.depths_summary?.hole_depth
        })),
        dataPoints: results.map((item) => ({
          x: item?.depths_summary?.[secondaryField?.field]?.[primaryField.field],
          y: item.bit_depth,
        })),
      };
    });
    const extractDataPints = graphData.map((item) => {
      return { dataPoints: item.dataPoints, };
    });
    const dataPointXs = extractDataPints.flatMap(v => v.dataPoints.map(p => p.x));
    let ppfpXValues = [];

    if (primaryField.field === 'ecd_with_sbp' || primaryField.field === 'esd_with_sbp') {
      ppfpXValues = window?.data?.flatMap(e => [e.pp, e.fp]) || [];
    }

    const allXs = [...dataPointXs, ...ppfpXValues];
    const minX = Math.min(...allXs);
    const maxX = Math.max(...allXs);

    setMinMax_X({
      minX,
      maxX
    });
    const depthSet = new Set();
    extractDataPints.forEach(run => run.dataPoints.forEach(({ y }) => depthSet.add(y)));
    const initialRun = extractDataPints[0]?.dataPoints;
    const isDescending = initialRun?.length > 1 && initialRun[0]?.y > initialRun[1]?.y;
    const sortedDepths = [...depthSet].sort((a, b) => isDescending ? b - a : a - b);

    const mapDataPoints = extractDataPints.map(run => {
      const map = new Map();
      run.dataPoints.forEach(({ x, y }) => map.set(y, x));
      return map;
    });

    const taleData = sortedDepths.map(bitDepth => {
      const entry = {
        bitDepth,
        _id: Math.random().toString(36).substring(2, 10)
      };
      mapDataPoints.forEach((map, idx) => {
        entry[`run${idx + 1}`] = map.get(bitDepth) ?? null;
      });
      return entry;
    });
    const columns_table = [
      {
        field: 'bitDepth',
        headerName: 'Bit Depth',
        renderHeader: () => (
          <Box>
            <Typography sx={{ fontSize: '0.875rem', fontWeight: 'bold' }}>
              Bit Depth ({strippingUnits?.results?.depth_points?.md?.label})
            </Typography>
          </Box>
        ),
        valueFormatter: (params) => {
          if (params.value === null || params.value === undefined || params.value === '') {
            return '';
          }
          const value = Number(params.value);
          return isNaN(value) ? params.value : value.toFixed(strippingUnits?.results?.depth_points?.bit_depth?.decimals);
        },
        width: 100,
        headerClassName: 'table-header',
        align: 'center',
        headerAlign: 'center',
      },
      ...graphData.map((col, index) => ({
        field: `run${index + 1}`,
        headerName: `Run ${index + 1}: ${col?.inputs?.trip_data?.speed || ''} ${strippingUnits?.inputs?.speed?.label} ${primaryField?.name ?? primaryField?.label} (${strippingUnits?.results?.depths_summary?.[secondaryField?.field]?.[primaryField?.field]?.label}) @ ${(secondaryField?.label)}  `,
        width: 200,
        renderHeader: () => (
          <Box>
            <Typography className={classes.headerStyle}>
              {`Run ${index + 1}: ${col?.inputs?.trip_data?.speed || ''} ${strippingUnits?.inputs?.speed?.label}`}
            </Typography>
            <Typography className={classes.headerStyle}>
              {`${primaryField?.name ?? primaryField?.label} (${strippingUnits?.results?.depths_summary?.[secondaryField?.field]?.[primaryField?.field]?.label})`}
            </Typography>
            <Typography className={classes.headerStyle}>
              {`@ ${(secondaryField?.label)}`}
            </Typography>
          </Box>
        ),
        headerAlign: 'center',
        align: 'center',
        headerClassName: 'table-header',
        valueFormatter: (params) => {
          if (params.value === null || params.value === undefined || params.value === '') {
            return '';
          }
          const value = Number(params.value);
          return isNaN(value) ? params.value : value.toFixed(strippingUnits?.results?.depths_summary?.[secondaryField?.field]?.[primaryField?.field]?.decimals);
        },
      })),
    ];
    setTableColumn(columns_table);
    setTableRows(taleData);
    const flatDataPoints = graphData.flatMap(item => item.dataPoints);
    const axisY = getMinMaxOfKey([...flatDataPoints], 'y');
    setMinMax_Y({
      min: axisY.min,
      max: axisY.max,
    });
    setChartData(graphData);
    setChartLegendData(graphData);
    setHiddenChartData(graphData);
  }, [strippingResults, plotForPrimary, plotForSecondary]);

  useEffect(() => {
    const filteredData = hiddenChartData.filter(item => {
      const row = chartLegendData.find(row => row.index === item.index);
      return !row?.hide;
    });
    setChartData(filteredData);
  }, [chartLegendData]);

  const generateWindowDataPoints = (config) => {
    const extractAllDepthPointsArrays = (data) => {
      return data.flatMap(item => item.depth_points);
    };
    const allDepthPoints = extractAllDepthPointsArrays(chartData);
    const dataPoints = [];

    if (window?.data.length === 1) {
      const singleDataPoint = window?.data[0];
      dataPoints.push(
        { x: singleDataPoint[config.key], y: 0, markerType: 'none' },
        { x: singleDataPoint[config.key], y: singleDataPoint.md },
        { x: singleDataPoint[config.key], y: allDepthPoints.at(-1).md, markerType: 'none' }
      );
    } else if (window?.data.length > 1) {
      const firstPoint = window?.data[0];
      const lastPoint = window?.data.at(-1);

      if (firstPoint.md > 0) {
        dataPoints.push({ x: firstPoint[config.key], y: 0, markerType: 'none' });
      }

      window?.data.forEach(item => {
        dataPoints.push({ x: item[config.key], y: item.md });
      });

      dataPoints.push({ x: lastPoint[config.key], y: allDepthPoints.at(-1).md, markerType: 'none' });
    }

    const formattedData = dataPoints.map(item => {
      const formattedItem = {
        x: parseFloat(Number(item.x).toFixed(windowUnits?.md?.decimals)),
        y: parseFloat(Number(item.y).toFixed(windowUnits?.pp?.decimals)),
      };
      if (item.markerType) {
        formattedItem.markerType = item.markerType;
      }
      return formattedItem;
    });
    return formattedData;
  };

  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.scrollTop = scrollPositionRef.current;
    }
  }, [chartData]);

  const CustomLegend = () => {
    const updateHide = (key, hideValue) => {
      handleScroll();
      setChartConfig(prevItems =>
        prevItems.map(item =>
          item.key === key ? { ...item, hide: hideValue } : item
        )
      );
    };

    const updateHideStrippingLegend = (key, hideValue) => {
      handleScroll();
      setChartLegendData(prevItems =>
        prevItems.map(item =>
          item.index === key ? { ...item, hide: hideValue } : item
        )
      );
    };
    return (
      <Grid container sx={{
        flexWrap: 'wrap',
        paddingLeft: '20px',
        height: 'auto',
        flexDirection: isMobile ? 'column' : 'row',
      }} ref={containerRef} onScroll={handleScroll} height={'30px'}>
        {(primaryField.field === 'esd_with_sbp' || primaryField.field === 'ecd_with_sbp') &&
          chartConfig.map(item => (
            <Box
              key={item.name}
              className={isMobile ? classes.legendItemMobile : classes.legendItem}
              sx={{ opacity: item.hide ? 0.4 : 1, width: 'fit-content' }}
              onClick={() => updateHide(item.key, !item.hide)}
            >
              {item.name}
              <LinearScaleOutlined fontSize="large" sx={{ color: item.color, mr: 1 }} />
            </Box>
          ))}
        {chartLegendData.map((item, index) => (
          <Box
            key={item.name + '' + index}
            className={isMobile ? classes.legendItemMobile : classes.legendItem}
            sx={{ opacity: item.hide ? 0.4 : 1, width: 'fit-content' }}
            onClick={() => updateHideStrippingLegend(item.index, !item.hide)}
          >
            {`${STRIPPING_RUN_MAP[index]}`}
            <IconButton
              sx={{
                '&::after': {
                  backgroundColor: item.color
                }
              }}
              className={classes.filterTableMarker}
            >
              {item?.markerType === 'triangle' && <ArrowDropUp sx={{ fontSize: isMobile ? 35 : 50, color: item.color }} />}
              {item?.markerType === 'circle' && <Circle sx={{ fontSize: isMobile ? 13 : 15, color: item.color }} />}
              {item?.markerType === 'square' && <Square sx={{ fontSize: isMobile ? 13 : 15, color: item.color }} />}
              {item?.markerType === 'cross' && <Close sx={{ fontSize: isMobile ? 20 : 25, color: item.color }} />}
            </IconButton>
          </Box>
        ))}
      </Grid>
    );
  };

  const handleChartAxisReset = (target) => {
    if (target === 'x' || target === 'resetAll') {
      setChartXAxis({ min: '', max: '' });
    }
    if (target === 'y' || target === 'resetAll') {
      setChartYAxis({ min: '', max: '' });
    }
  };

  const getValidValue = (val, fallback) =>
    val !== undefined && val !== null && val !== '' ? val : fallback;

  const xMin = getValidValue(chartXAxis?.min, minMax_X.minX - 0.1);
  const xMax = getValidValue(chartXAxis?.max, minMax_X.maxX + 0.1);
  const yMin = getValidValue(chartYAxis?.min, minMax_Y?.min - 100);
  const yMax = getValidValue(chartYAxis?.max, minMax_Y?.max + 100);

  const options = useMemo(() => ({
    theme: 'light2',
    animationEnabled: !isMobile,
    zoomEnabled: true,
    exportEnabled: true,
    zoomType: 'y',
    toolbar: {
      itemBackgroundColor: '#d3d3d3',
      itemBackgroundColorOnHover: '#3e3e3e',
      buttonBorderColor: '#3e3e3e',
    },
    backgroundColor: '#FFF',
    axisX: {
      viewportMinimum: xMin,
      viewportMaximum: xMax,
      interval: getOptimalInterval(xMin, xMax, 10),
      valueFormatString: '0.0',
      labelFontSize: isMobile ? 12 : 16,
      gridThickness: 1,
      title: `${primaryField?.name ?? primaryField?.label + ' @ ' + (secondaryField?.label)} (${strippingUnits?.results?.depths_summary?.[secondaryField?.field]?.[primaryField?.field]?.label})`,
      titleFontSize: isMobile ? 14 : 22,
    },
    axisY: {
      viewportMinimum: yMin,
      viewportMaximum: yMax,
      title: `Bit Depth (${strippingUnits?.results?.depth_points?.md?.label})`,
      titleFontSize: isMobile ? 14 : 22,
      labelFontSize: isMobile ? 12 : 16,
      includeZero: false,
      valueFormatString: '0',
      reversed: true,
      labelPlacement: 'outside',
      gridThickness: 1,
    },
    height: isMobile ? 520 : 900,
    legend: {
      fontSize: 20
    },
    data: [
      ...chartConfig.map((config) => {
        if (chartData.length > 0) {
          if (config.hide) return {};
          if (config.key === 'pp' || config.key === 'fp') {
            return {
              type: 'line',
              name: config.name,
              showInLegend: false,
              toolTipContent: config.tooltip,
              dataPoints: generateWindowDataPoints(config),
              color: config.color,
            };
          }
        }
        return {};
      }),
      ...chartData,
    ],
  }), [chartConfig, chartData, chartXAxis, chartYAxis]);

  const ChartAxisComponent = () => {
    return (
      <Box style={{ position: 'relative', zIndex: 20 }}>
        <Box className={classes.xAxisEditBtn}>
          <Box className={classes.editBox}>
            <ChartsAxisPopover
              title='X Axis'
              defaultMin={getValidValue(chartXAxis?.min, defaultXAxis.minX - 1)}
              defaultMax={getValidValue(chartXAxis?.max, defaultXAxis.maxX + 0.1)}
              handleReset
              onReset={() => handleChartAxisReset('x')}
              onSubmit={(min, max) => {
                setChartXAxis({ min: Number(min), max: Number(max) });
              }} />
          </Box>
        </Box>

        <Box className={classes.yAxisEditBtn}>
          <Box className={classes.editBox}>
            <ChartsAxisPopover
              title='Y Axis'
              defaultMin={getValidValue(chartYAxis?.min, defaultYAxis.min)}
              defaultMax={getValidValue(chartYAxis?.max, defaultYAxis.max)}
              onReset={() => handleChartAxisReset('y')}
              onSubmit={(min, max) => {
                setChartYAxis({ min: Number(min), max: Number(max) });
              }} />
          </Box>
        </Box>
      </Box>
    );
  };

  const TableFilterAndDetailsComponent = () => {
    if (isMobile) {
      return (
        <>
          <Box id="back-to-top" />
          <Grid
            container
            alignItems="center"
            justifyContent="space-between"
            sx={{ position: 'sticky', top: 0, background: COLORS.WHITE }}
          >
            <Box sx={{ p: 1 }} display="flex" alignItems="center">
              <FormControl
                fullWidth
                sx={{
                  bgcolor: 'transparent',
                  width: { xs: '130px', sm: '140px', md: '200px' },
                  height: { xs: '36px', sm: '40px', md: '48px' },
                }}
              >
                <InputLabel
                 id="primary-filter-label"
                 sx={{
                   fontSize: { xs: '12px', sm: '14px', md: '16px' },
                 }}
                >
                  Type
                </InputLabel>
                <Select
                  labelId="primary-filter-label"
                  id="primary-filter"
                  label="Type"
                  value={plotForPrimary}
                  onChange={(e) => {
                    handleChartAxisReset('resetAll');
                    setDefaultYAxis({ min: '', max: '' });
                    setDefaultXAxis({ min: '', max: '' });
                    setPlotForPrimary(e.target.value);
                  }}
                  sx={{
                    fontWeight: 'bold',
                    fontSize: { xs: '12px', sm: '14px', md: '18px' },
                    py: { xs: 0.2, sm: 0.5, md: 1 },
                    height: { xs: '32px', sm: '40px', md: '48px' },
                    background: 'unset',
                  }}
                >
                  {STRIPPING_TYPE_PRIMARY.map((item, idItem) => (
                    <MenuItem
                      key={`item_${item}_${idItem}`}
                      value={item.label}
                      sx={{
                        fontSize: { xs: '12px', sm: '14px', md: '18px' },
                        fontWeight: 'bold',
                        color: item?.color,
                      }}
                    >
                       {item?.name ?? item?.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Typography sx={{ mx: 1, fontWeight: 'bold', m: '0 10px' }}>at</Typography>

              <FormControl
                fullWidth
                sx={{
                  bgcolor: 'transparent',
                  width: { xs: '130px', sm: '140px', md: '160px' },
                  height: { xs: '36px', sm: '40px', md: '48px' },
                }}
              >
                <InputLabel
                id="secondary-filter-label"
                 sx={{
                   fontSize: { xs: '12px', sm: '14px', md: '16px' },
                 }}
                >
                  Depth
                </InputLabel>
                <Select
                  labelId="secondary-filter-label"
                  id="secondary-filter"
                  label="Depth"
                  value={plotForSecondary}
                  onChange={(e) => {
                    handleChartAxisReset('resetAll');
                    setDefaultXAxis({ min: '', max: '' });
                    setDefaultYAxis({ min: '', max: '' });
                    setPlotForSecondary(e.target.value);
                  }}
                  sx={{
                    fontWeight: 'bold',
                    fontSize: { xs: '12px', sm: '14px', md: '18px' },
                    py: { xs: 0.2, sm: 0.5, md: 1 },
                    height: { xs: '32px', sm: '40px', md: '48px' },
                    background: 'unset',
                  }}
                >
                  {secondaryDepthField.map((item, idItem) => (
                    <MenuItem
                      key={`item_${item}_${idItem}`}
                      value={item.label}
                      sx={{
                        fontSize: { xs: '12px', sm: '14px', md: '18px' },
                        fontWeight: 'bold',
                        color: item?.color,
                      }}
                    >
                      {item.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            <IconButton
              onClick={() => {
                setShowMobileChartTable(!showTable);
              }}
              size="small"
            >
              <CancelPresentationRounded fontSize="large" />
            </IconButton>
          </Grid>
        </>
      );
    }
    return (
      <Box display={'flex'} justifyContent={'start'} marginLeft={'1.25%'}>
        <Grid item xs={3}>
          <Box display="flex" justifyContent="space-around" marginLeft="1.25%" alignItems="center" width="400px">
            <Grid item>
              <FormControl fullWidth sx={{ bgcolor: 'transparent', width: '200px' }}>
                <InputLabel id="filter-input-label">Type</InputLabel>
                <Select
                  labelId="filter-select-id"
                  id="filter-id"
                  value={plotForPrimary}
                  label="Filter"
                  disabled={false}
                  onChange={(e) => {
                    handleChartAxisReset('resetAll');
                    setDefaultYAxis({ min: '', max: '' });
                    setDefaultXAxis({ min: '', max: '' });
                    setPlotForPrimary(e.target.value);
                  }}
                  sx={{
                    fontWeight: 'bolder',
                    fontSize: '18px',
                    background: 'unset'
                  }}
                >
                  {STRIPPING_TYPE_PRIMARY.map((item, idItem) => (
                    <MenuItem
                      key={`item_${item}_${idItem}`}
                      value={item.label}
                      sx={{ color: item?.color, fontSize: '18px', fontWeight: 'bold' }}
                    >
                       {item?.name ?? item?.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item>
              <Typography sx={{ fontWeight: 'bold', fontSize: '1rem', m: '0 10px' }}>
                {'at'}
              </Typography>
            </Grid>
            <Grid item>
              <FormControl fullWidth sx={{ bgcolor: 'transparent', width: '200px' }}>
                <InputLabel id="filter-input-label">Depth</InputLabel>
                <Select
                  labelId="filter-select-id"
                  id="filter-id"
                  value={plotForSecondary}
                  label="Filter"
                  disabled={false}
                  onChange={(e) => {
                    handleChartAxisReset('resetAll');
                    setDefaultXAxis({ min: '', max: '' });
                    setDefaultYAxis({ min: '', max: '' });
                    setPlotForSecondary(e.target.value);
                  }}
                  sx={{
                    fontWeight: 'bolder',
                    fontSize: '18px',
                    background: 'unset'
                  }}
                >
                  {secondaryDepthField.map((item, idItem) => (
                    <MenuItem
                      key={`item_${item}_${idItem}`}
                      value={item.label}
                      sx={{ color: item?.color, fontSize: '18px', fontWeight: 'bold' }}
                    >
                      {item.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Box>
        </Grid>
      </Box>
    );
  };

  const handleScrollToTop = (event) => {
    const anchor = (event.target.ownerDocument || document).querySelector(
      '#back-to-top',
    );

    if (anchor) {
      anchor.scrollIntoView({
        block: 'start',
        behavior: 'smooth',
      });
    }
  };

  const ChartTableComponent = () => {
    if (isMobile) {
      return (
        <Drawer
          open={showMobileChartTable}
          onClose={() => setShowMobileChartTable(false)}
          sx={{
            height: '100vh',
          }}
          PaperProps={{ style: { position: 'absolute', width: '100%' } }}
          variant='temporary'
        >
          <TableFilterAndDetailsComponent />
          <Grid item xs={12}>
            {tableRow.map((row, index) => (
              <Box key={index} className={classes.gridContainer}>
                {tableColumn.map((col, idx) => {
                  const value = col?.valueGetter
                    ? col.valueGetter({ row })
                    : col?.valueFormatter
                      ? col.valueFormatter({
                        row,
                        field: col.field,
                        value: row[col.field],
                      })
                      : row[col.field];
                  return (
                    <Grid container key={idx} className={classes.gridItem}>
                      <Typography className={classes.headerName} variant="body1">
                        {col.headerName}
                      </Typography>
                      <Typography sx={{ flex: 0.5 }} variant="body1">{value}</Typography>
                    </Grid>
                  );
                })}
              </Box>
            ))}
          </Grid>
          <Fab size='small' className={classes.accordianScrollUp} onClick={handleScrollToTop}>
            <KeyboardArrowUp sx={{ fontSize: '30px' }} />
          </Fab>
        </Drawer>
      );
    }
    return (
      <>
        <TableFilterAndDetailsComponent />
        <Datagrid
          loading={false}
          data={tableRow}
          sx={{
            '.MuiDataGrid-cell': {
              fontSize: '1rem',
              padding: '2px',
            },
            '.MuiDataGrid-columnHeader': {
              fontSize: '1.2rem',
              fontWeight: '900 !important',
            },
            '& .MuiDataGrid-columnHeaderTitle': {
              overflow: 'visible',
              lineHeight: '1.43rem',
              whiteSpace: 'normal',
            },
            '& .MuiDataGrid-footerContainer': {
              minHeight: '0px'
            },
            '& .MuiDataGrid-cell:focus-within, & .MuiDataGrid-cell:focus': {
              outline: 'none !important',
            },
            '& .MuiDataGrid-columnHeader:focus-within, & .MuiDataGrid-columnHeader:focus':
            {
              outline: 'none !important',
              height: '200px'
            },
          }}
          columns={tableColumn}
          disableRowSelectionOnClick
          disableStickyHeader={true}
          autoHeight={false}
          height={'800px'}
          getRowHeight={() => 'auto'}
          pageSize={100}
          {...(tableRow.length <= 100
            ? { hideFooterPagination: true, hideFooterSelectedRowCount: true }
            : {})}
          toolbar={{
            options: {
              columns: false,
              filters: false,
              density: false,
              export: {
                enabled: true,
              }
            },
          }}
        />
      </>
    );
  };

  if (isMobile) {
    if (!tableRow?.length) return null;
    return (
      <Box sx={{ pb: 3 }}>
        {!showMobileChartTable && (
          <Grid item container sx={{ display: showMobileChartTable && 'none' }} xs={12} marginTop={'17px'} alignItems="center" justifyContent="center">
            <CustomLegend />
            <Grid item xs={12}>
              <Box
                sx={{
                  position: 'relative'
                }}
              >
                {/* scroll box on the left, so chart has a way to scroll out on mobile */}
                <Box
                  sx={{
                    position: 'absolute',
                    left: 0,
                    top: 0,
                    width: 55,
                    height: '100%',
                    touchAction: 'auto',
                    zIndex: 10,
                    background: 'transparent',
                  }}
                />
                <Box
                  sx={{
                    touchAction: 'none'
                  }}
                >
                  <CanvasJSChart options={options} />
                </Box>
              </Box>
              <ChartAxisComponent />
            </Grid>
            <Box className={classes.menuTogglerMobileBtn} sx={{ display: 'none' }}>
              <IconButton
                onClick={() => {
                  setShowMobileChartTable(!showMobileChartTable);
                }}>
                <ChevronLeft sx={{ fontSize: '25px' }} />
              </IconButton>
            </Box>
          </Grid>
        )}
        {showMobileChartTable && (
          <Grid item container sx={{ display: !showMobileChartTable && 'none' }} xs={12} marginTop={'17px'} alignItems="center" justifyContent="center">
            {tableColumn && <ChartTableComponent />}
            <Box className={classes.menuTogglerMobileTableBtn}>
              <IconButton
                onClick={() => {
                  setShowMobileChartTable(!showMobileChartTable);
                }}>
                <ChevronRight sx={{ fontSize: '25px' }} />
              </IconButton>
            </Box>
          </Grid>
        )}
      </Box>
    );
  }

  return (
    <>
      <Grid container item xs={showTable ? 7 : 12} marginTop={'1%'} alignItems="center" justifyContent="center">
        <Grid item display="flex">
          {<CustomLegend />}
        </Grid>
      </Grid>
      <Box style={{
        overflow: 'auto',
      }}>
        <Grid container>
          <Grid item xs={showTable ? 7 : 12} sx={{
            p: 3,
            pt: 4,
            '.canvasjs-chart-toolbar': {
              top: '-20px !important',
            },
          }}>
            <Box
              sx={{
                position: 'relative'
              }}
            >
              {/* scroll box on the left, so chart has a way to scroll out on mobile */}
              <Box
                sx={{
                  position: 'absolute',
                  left: 0,
                  top: 0,
                  width: 55,
                  height: '100%',
                  touchAction: 'auto',
                  zIndex: 10,
                  background: 'transparent',
                }}
              />
              <Box
                sx={{
                  touchAction: 'none'
                }}
              >
                <CanvasJSChart options={options} />
              </Box>
            </Box>
            <ChartAxisComponent />
          </Grid>
          {!showTable && <Box className={classes.menuTogglerBtn}>
            <IconButton
              onClick={() => {
                setShowTable(!showTable);
              }}>
              <ChevronLeft sx={{ fontSize: '33px' }} />
            </IconButton>
          </Box>}
          {showTable && <Grid item xs={5} marginTop={'1.5%'}>
            <Box className={classes.menuTogglerTableBtn}>
              <IconButton
                onClick={() => {
                  setShowTable(!showTable);
                }}>
                <ChevronRight sx={{ fontSize: '33px' }} />
              </IconButton>
            </Box>

            {showTable && <Grid item xs={12}>
              <ChartTableComponent />
            </Grid>}
          </Grid>}
        </Grid>
      </Box>
    </>
  );
};
FeaturedWellStrippingChart.propTypes = {
  data: PropTypes.any,
  window: PropTypes.any,
  units: PropTypes.any
};

export default FeaturedWellStrippingChart;
